import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Loader2, TrendingUp, Eye, Users, Smartphone, Monitor, Tablet, BarChart3, Filter } from "lucide-react";

const FRIENDLY_PATHS = {
  "/": "Homepage",
  "/terms": "Terms & Conditions",
};

function friendlyPath(p) {
  if (FRIENDLY_PATHS[p]) return FRIENDLY_PATHS[p];
  if (p.startsWith("/register/")) return `Registration · ${p.replace("/register/", "")}`;
  if (p.startsWith("/confirmation/")) return "Confirmation page";
  return p;
}

function StatCard({ label, value, sub, color = "text-neutral-100" }) {
  return (
    <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-4">
      <div className="text-[10px] tracking-[0.18em] uppercase text-neutral-500">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-neutral-500 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function AnalyticsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/analytics");
      setData(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return (
      <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 flex items-center gap-3 text-neutral-400 mb-6">
        <Loader2 className="animate-spin" size={16} /> Loading analytics…
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="bg-neutral-900 border border-red-700 rounded-2xl p-6 text-red-300 mb-6" data-testid="analytics-error">
        {err || "No data"}
      </div>
    );
  }

  const maxDaily = Math.max(1, ...data.daily.map((d) => d.views));
  const totalDevice = Object.values(data.device).reduce((a, b) => a + b, 0) || 1;

  return (
    <div className="mb-8" data-testid="analytics-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[11px] tracking-[0.25em] uppercase font-mono text-[#FF6A1A]/80">Website Traffic</div>
          <h2 className="text-2xl font-semibold mt-0.5 flex items-center gap-2">
            <BarChart3 size={20} className="text-[#FF6A1A]" /> Analytics
          </h2>
        </div>
        <button
          onClick={load}
          className="text-xs bg-neutral-800 text-neutral-200 px-3 py-1.5 rounded-full hover:bg-neutral-700"
          data-testid="analytics-refresh"
        >
          Refresh
        </button>
      </div>

      {/* Top stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        <StatCard
          label="Today"
          value={data.today.uniques}
          sub={`${data.today.views} views`}
          color="text-[#FF6A1A]"
        />
        <StatCard label="This week" value={data.week.uniques} sub={`${data.week.views} views`} />
        <StatCard label="This month" value={data.month.uniques} sub={`${data.month.views} views`} />
        <StatCard label="All time" value={data.totals.uniques} sub={`${data.totals.views} views`} />
        <StatCard
          label="Conversion"
          value={`${data.conversion_rate}%`}
          sub={`${data.applications_total} applications`}
          color="text-green-400"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Last 14 days bar chart */}
        <div className="lg:col-span-2 bg-neutral-900 border border-neutral-800 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={14} className="text-[#FF6A1A]" />
            <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Last 14 days</div>
          </div>
          <div className="flex items-end gap-1.5 h-32" data-testid="analytics-daily-chart">
            {data.daily.map((d) => {
              const h = Math.max(2, Math.round((d.views / maxDaily) * 110));
              const label = d.day.slice(5); // MM-DD
              const isToday = d.day === data.daily[data.daily.length - 1].day;
              return (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                  <div className="text-[9px] text-neutral-400 font-mono">{d.views || ""}</div>
                  <div
                    className={`w-full rounded-t ${isToday ? "bg-[#FF6A1A]" : "bg-[#FF6A1A]/40"}`}
                    style={{ height: `${h}px` }}
                    title={`${d.day}: ${d.views} views, ${d.uniques} unique`}
                  />
                  <div className="text-[9px] text-neutral-500 font-mono">{label}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Device split */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-4">
            <Smartphone size={14} className="text-[#FF6A1A]" />
            <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Devices</div>
          </div>
          <div className="space-y-3" data-testid="analytics-devices">
            {["mobile", "desktop", "tablet"].map((kind) => {
              const n = data.device[kind] || 0;
              const pct = Math.round((n / totalDevice) * 100);
              const Icon = kind === "mobile" ? Smartphone : kind === "tablet" ? Tablet : Monitor;
              return (
                <div key={kind}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 capitalize">
                      <Icon size={12} className="text-neutral-400" />
                      {kind}
                    </span>
                    <span className="text-neutral-400">
                      {n} · {pct}%
                    </span>
                  </div>
                  <div className="h-1.5 bg-neutral-800 rounded-full mt-1.5 overflow-hidden">
                    <div className="h-full bg-[#FF6A1A]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Per-page breakdown */}
      <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-4 mt-4">
        <div className="flex items-center gap-2 mb-4">
          <Eye size={14} className="text-[#FF6A1A]" />
          <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Top pages</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="analytics-pages-table">
            <thead className="text-left text-[10px] tracking-[0.18em] uppercase text-neutral-500">
              <tr>
                <th className="pb-2 font-medium">Page</th>
                <th className="pb-2 font-medium text-right">Views</th>
                <th className="pb-2 font-medium text-right">Unique</th>
              </tr>
            </thead>
            <tbody>
              {data.per_page.length === 0 && (
                <tr><td colSpan={3} className="py-4 text-center text-neutral-500">No visits yet.</td></tr>
              )}
              {data.per_page.map((p) => (
                <tr key={p.path} className="border-t border-neutral-800">
                  <td className="py-2 text-neutral-200">
                    <div className="font-medium">{friendlyPath(p.path)}</div>
                    <div className="text-[10px] text-neutral-500 font-mono">{p.path}</div>
                  </td>
                  <td className="py-2 text-right font-mono">{p.views}</td>
                  <td className="py-2 text-right font-mono">
                    <span className="inline-flex items-center gap-1 text-[#FF6A1A]">
                      <Users size={11} /> {p.uniques}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Conversion funnel */}
      {data.funnel && data.funnel.length > 0 && (
        <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-4 mt-4" data-testid="analytics-funnel">
          <div className="flex items-center gap-2 mb-4">
            <Filter size={14} className="text-[#FF6A1A]" />
            <div className="text-[10px] tracking-[0.2em] uppercase text-neutral-500">Conversie funnel</div>
          </div>
          <FunnelChart steps={data.funnel} />
          <p className="mt-3 text-[10px] text-neutral-500 leading-relaxed">
            Zie precies waar bezoekers afhaken. Percentages tonen retentie vs. de vorige stap.
          </p>
        </div>
      )}

      <p className="mt-3 text-[10px] text-neutral-500 leading-relaxed">
        Privacy-friendly tracking. No cookies, no personal data. Unique counts use hashed visitor fingerprints (rotated daily).
      </p>
    </div>
  );
}

function FunnelChart({ steps }) {
  const max = Math.max(1, ...steps.map((s) => s.count));
  return (
    <div className="space-y-2">
      {steps.map((s, i) => {
        const prev = i === 0 ? null : steps[i - 1].count;
        const dropPct = prev && prev > 0 ? Math.round((s.count / prev) * 100) : null;
        const width = Math.max(4, Math.round((s.count / max) * 100));
        return (
          <div key={s.key} className="flex items-center gap-3">
            <div className="w-2/5 shrink-0 text-xs font-medium text-neutral-200">
              {i + 1}. {s.label}
              {dropPct !== null && (
                <span className={`ml-2 text-[10px] font-mono ${dropPct < 40 ? "text-red-400" : dropPct < 70 ? "text-yellow-400" : "text-green-400"}`}>
                  {dropPct}%
                </span>
              )}
            </div>
            <div className="flex-1 h-6 bg-neutral-800 rounded-md overflow-hidden">
              <div className="h-full bg-[#FF6A1A] flex items-center justify-end px-2 text-[10px] font-mono text-black font-bold" style={{ width: `${width}%` }}>
                {s.count}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
